import Button from "../../components/Button";
import Content from "../../content";
import ContentfulClient from "../../lib/contentful_client/ContentfulClient";
import FoundryLink from "../../components/FoundryLink";
import { FoundryOrder } from "../../lib/foundry_client/models/FoundryOrder";
import Head from "next/head";
import Login from "../../components/auth/Login";
import OrderBreakdown from "../../components/checkout/OrderBreakdown";
import { PageFadeWrapper } from "../../components/PageWrapper";
import Routes from "../../routes";
import Separator from "../../components/Separator";
import ShoppingHeader from "../../components/cart/ShoppingHeader";
import { authOptions } from "../api/auth/[...nextauth]";
import dayjs from "dayjs";
import { formatMoney } from "../../util/strings";
import { getServerSession } from "next-auth";
import { push } from "../../util/routing";
import { signOut } from "next-auth/react";
import { useRouter } from "next/router";
import { useState } from "react";

interface Props {
  loggedIn: any;
  user: {
    name?: string;
    email?: string;
  };
  orders: FoundryOrder[];
}

const Wrapper = (any: any) => {
  return (
    <>
      <Head>
        <title>Your Account | {Content.siteName}</title>
      </Head>
      <PageFadeWrapper>
        <section className="nav-spacer container-full w-full flex flex-col">
          <div className="wild rounded-lg" />
          <div className="z-10 flex flex-row justify-center items-start">
            {any}
          </div>
        </section>
      </PageFadeWrapper>
    </>
  );
};

const OrderTable = (orders: FoundryOrder[]) => {
  return (
    <table className="table-fixed min-w-full">
      <tbody>
        <tr>
          <th>Date</th>
          <th>Order Number</th>
          <th>Total</th>
        </tr>
        {orders.map((order) => (
          <OrderRow order={order} />
        ))}
      </tbody>
    </table>
  );
};

const OrderRow = ({ order }: { order: FoundryOrder }) => {
  const [checked, setChecked] = useState(false);
  const router = useRouter();

  return (
    <>
      <tr
        className={`cursor-pointer ${
          checked
            ? "bg-fedblue color-secondary border-0"
            : "bg-neutral-500-hover"
        }`}
        onClick={() => setChecked(!checked)}
      >
        <td>
          {dayjs(order.completedAt || order.createdAt).format("MMM DD, YYYY")}
        </td>
        <td>
          <FoundryLink
            href={Routes.account.order(order.friendlyId)}
            onClick={(e) => {
              e.stopPropagation();
              push(router, Routes.account.order(order.friendlyId));
            }}
          >
            <span className="underline color-blue-hover">
              {order.friendlyId}
            </span>
          </FoundryLink>
        </td>
        <td>{formatMoney("USD", order.amountPaid || 0)}</td>
      </tr>

      {checked && (
        <tr>
          <td colSpan={3} className="text-start pt-2 b-4">
            <div className="border p-8 flex flex-col gap-8">
              <OrderBreakdown order={order} />
              <Button
                onClick={() =>
                  push(router, Routes.account.order(order.friendlyId))
                }
                classes="py-6 border bg-fedblue-hover border-fedblue-hover color-secondary-hover transition-colors"
              >
                View full details
              </Button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

function AccountIndex(props: Props) {
  const router = useRouter();

  if (!props.loggedIn) {
    return Wrapper(
      <section
        className="flex justify-center items-start mt-24"
        style={{ minHeight: "calc(100vh - 2*var(--nav-spacing))" }}
      >
        <div className="p-10 rounded-lg shadow-lg max-w-lg bg-neutral-700">
          <h2 className="t5 pb-2">Sign in to your account</h2>
          <p className="text-sm">
            Enter your email address and we'll send you a link to complete your
            login.
          </p>
          <Login />
        </div>
      </section>
    );
  }

  return (
    <>
      <Head>
        <title>Your Account | {Content.siteName}</title>
      </Head>
      <PageFadeWrapper>
        <ShoppingHeader
          title="Your Account"
          hidePackageDiscounts={true}
          containerClass="container"
        />
        <section className="container w-full flex flex-col pb-24">
          <div className="max-w-4xl">
            <section className="pt-12">
              <h2 className="t4 mt-4">Account Details</h2>
              <Separator hideLeft={true} />
              <p className="mb-2">You are signed in as {props.user.email}.</p>
              <Button
                border={false}
                classes="px-8 border bg-pink-hover border-pink-hover transition-colors"
                onClick={async (ev) => {
                  ev.preventDefault();
                  signOut();
                }}
              >
                Log out
              </Button>
              <h2 className="t4 mt-16">Order History</h2>
              <Separator hideLeft={true} />
              <p className="mb-6">
                You can view your past orders below and download fonts through
                the order details.
              </p>
              {OrderTable(props.orders)}
            </section>
          </div>
        </section>
      </PageFadeWrapper>
    </>
  );
}

export async function getServerSideProps(context) {
  // @ts-ignore idk why it's mad
  const session = await getServerSession(context.req, context.res, authOptions);

  let orders: FoundryOrder[] = [];

  if (session && session.user?.email) {
    orders = await ContentfulClient.getOrdersByEmail(session.user?.email);
  }

  return {
    props: {
      loggedIn: !!session,
      user: {
        name: session?.user?.name || null,
        email: session?.user?.email || null,
      },
      orders,
    },
  };
}

AccountIndex.displayName = "AccountIndex";
export default AccountIndex;
